import { useState, useCallback, useEffect } from "react";

interface Voice {
  voice: SpeechSynthesisVoice;
  name: string;
  lang: string;
  localName: string;
}

export const useTTS = () => {
  const [voices, setVoices] = useState<Voice[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<SpeechSynthesisVoice | null>(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [rate, setRate] = useState(1);
  const [pitch, setPitch] = useState(1);

  useEffect(() => {
    const loadVoices = () => {
      const availableVoices = window.speechSynthesis.getVoices();
      const formattedVoices: Voice[] = availableVoices.map((voice) => ({
        voice,
        name: voice.name,
        lang: voice.lang,
        localName: voice.localService ? `${voice.name} (محلي)` : voice.name,
      }));
      
      setVoices(formattedVoices);
      
      // Try to find an Arabic voice first, then English
      const arabicVoice = availableVoices.find((v) => v.lang.startsWith("ar"));
      const englishVoice = availableVoices.find((v) => v.lang.startsWith("en"));
      setSelectedVoice(arabicVoice || englishVoice || availableVoices[0] || null);
    };

    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;

    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, []);

  const speak = useCallback((text: string) => {
    if (!text || !selectedVoice) return;

    // Cancel any ongoing speech
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.voice = selectedVoice;
    utterance.rate = rate;
    utterance.pitch = pitch;
    utterance.lang = selectedVoice.lang;

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    window.speechSynthesis.speak(utterance);
  }, [selectedVoice, rate, pitch]);

  const stop = useCallback(() => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  }, []);

  const pause = useCallback(() => {
    window.speechSynthesis.pause();
  }, []);

  const resume = useCallback(() => {
    window.speechSynthesis.resume();
  }, []);

  return {
    voices,
    selectedVoice,
    setSelectedVoice,
    isSpeaking,
    rate,
    setRate,
    pitch,
    setPitch,
    speak,
    stop,
    pause,
    resume,
  };
};
